import React, { type SyntheticEvent, useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { expValEquals, UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { getFeatureMap } from '../../../../common/constants.tsx';
import messages from './messages.tsx';
import type { FeatureItemProps } from './types.tsx';

export const FeatureToggleItem = ({
	feature,
	state,
	isToggleLocked,
	localisedName,
	onItemToggle,
}: FeatureItemProps) => {
	const { formatMessage } = useIntl();
	const { icon, descriptionKey } = useMemo(() => {
		const [expConfig] = UNSAFE_noExposureExp('jsw_simplify_navigation_m2');

		const featureMap = getFeatureMap({
			isSimplifyNavExperimentEnabled: expConfig.get('isEnabled', false),
			isListViewEnabled: expValEquals('jsw_list_view', 'cohort', 'variation'),
		});

		return featureMap[feature];
	}, [feature]);

	const handleChange = useCallback(
		(_e: SyntheticEvent<HTMLInputElement>, analyticsEvent: UIAnalyticsEvent) => {
			onItemToggle({ featureId: feature, state, analyticsEvent });
		},
		[feature, onItemToggle, state],
	);

	return (
		<ItemContainer>
			<Box xcss={itemIconContainerStyles}>{icon}</Box>
			<ItemTextContainer>
				<ItemTitle>{localisedName}</ItemTitle>
				<FeatureDescriptionContainer>
					{/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
					{formatMessage(messages[descriptionKey as keyof typeof messages])}
				</FeatureDescriptionContainer>
			</ItemTextContainer>

			<Toggle
				testId={`tmp-feature-toggle.ui.feature-popup-content.feature-toggle-group.${feature}.toggle`}
				label={`${feature}-label`}
				isDisabled={isToggleLocked}
				isChecked={state === 'ENABLED'}
				onChange={handleChange}
				id={`${feature}-toggle`}
			/>
		</ItemContainer>
	);
};

const itemIconContainerStyles = xcss({
	paddingRight: 'space.150',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemTitle = styled.p({
	paddingTop: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: '380px',
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemTextContainer = styled.div({
	flex: '1 1 auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeatureDescriptionContainer = styled.div({
	marginTop: token('space.050'),
	paddingRight: token('space.200'),
	flex: '1 1 auto',
	color: token('color.text.subtle'),
});
